export const formatToVttTime = (seconds) => {
  const ms = (seconds * 1000) % 1000;
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor((seconds / (60 * 60)) % 24);
  const mss = (`${ms}00`).slice(0, 3);
  const ss = (`0${s}`).slice(-2);
  const mm = (`0${m}`).slice(-2);
  const hh = (`0${h}`).slice(-2);
  return `${hh}:${mm}:${ss}.${mss}`;
};

const splitAtWordBoundary = (cue, maxChars) => {
  const words = cue.text.split(' ');
  const duration = cue.endTime - cue.startTime;
  const textLength = cue.text.length;
  const cues = [];
  let currentText = '';
  let currentStart = cue.startTime;
  let cueCount = 0;

  words.forEach((word) => {
    if (currentText.length + word.length + 1 > maxChars && currentText.length > 0) {
      const ratio = currentText.length / textLength;
      const currentEnd = cue.startTime + (ratio * duration);
      cues.push({
        id: `${cue.id}_sub_${cueCount}`,
        startTime: currentStart,
        endTime: currentEnd,
        text: currentText.trim()
      });
      cueCount += 1;
      currentStart = currentEnd;
      currentText = word;
    } else {
      currentText += (currentText ? ' ' : '') + word;
    }
  });

  // Add the last cue
  if (currentText.length > 0) {
    cues.push({
      id: `${cue.id}_sub_${cueCount}`,
      startTime: currentStart,
      endTime: cue.endTime,
      text: currentText.trim()
    });
  }

  return cues;
};

const splitByCharCount = (text, startTime, endTime, idPrefix, maxChars) => {
  if (text.length <= maxChars) {
    return [{
      id: idPrefix,
      startTime,
      endTime,
      text
    }];
  }

  const sentences = text.split(/[.!?]\s+/); // Split by sentence boundaries
  const duration = endTime - startTime;
  const textLength = text.length;
  const cues = [];
  let currentCue = '';
  let currentStart = startTime;
  let cueCount = 0;

  sentences.forEach((sentence) => {
    // If adding this sentence exceeds max chars, create a new cue
    if (currentCue.length + sentence.length > maxChars && currentCue.length > 0) {
      const ratio = currentCue.length / textLength;
      const currentEnd = startTime + (ratio * duration);
      cues.push({
        id: `${idPrefix}_${cueCount}`,
        startTime: currentStart,
        endTime: currentEnd,
        text: currentCue.trim()
      });
      cueCount += 1;
      currentStart = currentEnd;
      currentCue = sentence;
    } else {
      currentCue += (currentCue ? ' ' : '') + sentence;
    }
  });

  // Add the last cue if there's any text left
  if (currentCue.length > 0) {
    cues.push({
      id: `${idPrefix}_${cueCount}`,
      startTime: currentStart,
      endTime,
      text: currentCue.trim()
    });
  }

  // If any cue is still too long, split it at a word boundary
  return cues.reduce(
    (acc, cue) => (cue.text.length > maxChars
      ? [...acc, ...splitAtWordBoundary(cue, maxChars)]
      : [...acc, cue]),
    []
  );
};

const splitUtterance = (utterance, intervalMs = 10000, maxCharsPerCue = 300) => {
  const start = Number(utterance.startTime);
  const end = Number(utterance.endTime);
  const duration = end - start;
  const text = utterance.text;

  // If short enough in both time and length, keep as is
  if (duration <= intervalMs && text.length <= maxCharsPerCue) {
    return [utterance];
  }

  // If duration is long enough to split by time
  if (duration > intervalMs) {
    const numParts = Math.ceil(duration / intervalMs);
    const timePortion = duration / numParts;
    const words = text.split(' ');
    const wordsPerPart = Math.ceil(words.length / numParts);

    return Array.from({ length: numParts }, (_, i) => {
      const startIdx = i * wordsPerPart;
      const endIdx = Math.min((i + 1) * wordsPerPart, words.length);
      const textPart = words.slice(startIdx, endIdx).join(' ');
      const cueStart = start + (i * timePortion);
      const cueEnd = start + ((i + 1) * timePortion);

      // Further split by character count if needed
      return splitByCharCount(textPart, cueStart, cueEnd, `${utterance.id}_${i}`, maxCharsPerCue);
    }).flat();
  }

  // Just split by character count
  return splitByCharCount(text, start, end, utterance.id, maxCharsPerCue);
};

function createVttFromWords(sentences) {
  // Flatten all cues after splitting
  const allCues = sentences.reduce((acc, s) => [...acc, ...splitUtterance(s)], []);
  const sorted = [...allCues].sort((a, b) => a.startTime - b.startTime);

  const webVttString = sorted.reduce((finalString, sentence) => {
    const fromTime = formatToVttTime(sentence.startTime / 1000);
    const toTime = formatToVttTime(sentence.endTime / 1000);
    return `${finalString}${fromTime} --> ${toTime}\n${sentence.text}\n\n`;
  }, 'WEBVTT\n\n');

  return URL.createObjectURL(new Blob([webVttString], { type: 'text/vtt' }));
}

export const generateVttFromStt = (utterances) => createVttFromWords(Object.values(utterances));
